import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  Phone, ShieldCheck, MapPin, Play, LogOut, RefreshCw,
  Navigation, CheckCircle2, Loader2, ArrowRight, Share, Radio,
} from "lucide-react";
import { driverApi, DRIVER_TOKEN_KEY } from "../../lib/driverApi";
import { Wordmark, LogoMark } from "./Logo";
import { logError } from "../../lib/logError";

const PING_MS = 30000;

export default function DriverApp() {
  const [driver, setDriver] = useState(null);
  const [booting, setBooting] = useState(true);

  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const [trips, setTrips] = useState([]);
  const [sharingTn, setSharingTn] = useState(null);
  const timer = useRef(null);

  const loadTrips = useCallback(async () => {
    try { const { data } = await driverApi.get("/driver/trips"); setTrips(data); } catch (e) { logError("driver.loadTrips", e); }
  }, []);

  useEffect(() => {
    const t = localStorage.getItem(DRIVER_TOKEN_KEY);
    if (!t) { setBooting(false); return; }
    driverApi.get("/driver/me")
      .then(({ data }) => { setDriver(data); loadTrips(); })
      .catch(() => localStorage.removeItem(DRIVER_TOKEN_KEY))
      .finally(() => setBooting(false));
  }, [loadTrips]);

  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);

  const login = async () => {
    if (!phone.trim() || !pin.trim()) return toast.error("Enter your phone number and access code");
    setBusy(true);
    try {
      const { data } = await driverApi.post("/driver/login", { phone, pin });
      localStorage.setItem(DRIVER_TOKEN_KEY, data.token);
      setDriver(data.driver);
      loadTrips();
      toast.success(`Welcome, ${data.driver.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid phone or access code"); }
    finally { setBusy(false); }
  };

  const logout = () => {
    if (timer.current) clearInterval(timer.current);
    setSharingTn(null);
    localStorage.removeItem(DRIVER_TOKEN_KEY);
    setDriver(null); setPhone(""); setPin(""); setTrips([]);
  };

  const pushLocation = useCallback((tn) => {
    if (!navigator.geolocation) return toast.error("Location not supported on this device");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try { await driverApi.post(`/driver/trips/${tn}/location`, { lat: pos.coords.latitude, lng: pos.coords.longitude }); }
        catch (e) { logError("driver.pushLocation", e); }
      },
      () => toast.error("Enable location permission to share your position"),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
    );
  }, []);

  const startSharing = useCallback((tn) => {
    setSharingTn(tn);
    pushLocation(tn);
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => pushLocation(tn), PING_MS);
  }, [pushLocation]);

  const stopSharing = () => { if (timer.current) clearInterval(timer.current); timer.current = null; setSharingTn(null); };

  const startTrip = async (tn) => {
    try {
      await driverApi.post(`/driver/trips/${tn}/start`);
      toast.success("Trip started — sharing your live location");
      startSharing(tn);
      loadTrips();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start trip"); }
  };

  const advance = async (t) => {
    const next = Math.min((t.current_index || 0) + 1, t.stops.length - 1);
    const delivered = next >= t.stops.length - 1;
    try {
      await driverApi.post(`/driver/trips/${t.tracking_number}/status`,
        { current_index: next, status: delivered ? "Delivered" : "In Transit" });
      toast.success(delivered ? "Marked as delivered ✅" : `Checked in at ${t.stops[next]}`);
      if (delivered) stopSharing();
      loadTrips();
    } catch { toast.error("Could not update"); }
  };

  if (booting) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  // -------- LOGIN --------
  if (!driver) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="w-full max-w-sm">
            <div className="flex justify-center">
              <Wordmark sizeClass="h-9" />
            </div>
            <p className="text-center text-muted-foreground text-sm mt-2">Driver App</p>

            <div className="mt-10 rounded-3xl border border-border bg-card/40 p-6">
              <h1 className="font-heading font-bold text-xl">Driver sign in</h1>
              <p className="text-sm text-muted-foreground mt-1">Use the phone number and access code your dispatcher gave you.</p>
              <label className="mt-6 block text-xs text-muted-foreground">Phone number</label>
              <div className="mt-1 flex items-center gap-2 rounded-xl border border-border bg-background/60 px-4">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <input data-testid="driver-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 437 808 2496"
                  className="flex-1 bg-transparent py-3 outline-none" />
              </div>
              <label className="mt-4 block text-xs text-muted-foreground">Access code</label>
              <div className="mt-1 flex items-center gap-2 rounded-xl border border-border bg-background/60 px-4">
                <ShieldCheck className="w-4 h-4 text-muted-foreground" />
                <input data-testid="driver-pin-input" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Access code"
                  onKeyDown={(e) => e.key === "Enter" && login()} className="flex-1 bg-transparent py-3 outline-none tracking-[0.3em] font-mono-x" />
              </div>
              <button data-testid="driver-login-btn" onClick={login} disabled={busy}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-3.5 disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>

            <div className="mt-6 flex items-start gap-2 text-xs text-muted-foreground">
              <Share className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Install this app: tap the Share icon in your browser, then <b>Add to Home Screen</b>.</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // -------- DASHBOARD --------
  return (
    <div className="min-h-screen bg-background text-foreground pb-10">
      <header className="sticky top-0 z-40 bg-background/90 backdrop-blur border-b border-border">
        <div className="max-w-xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-extrabold"><LogoMark className="w-6 h-6" /> Driver</div>
          <div className="flex items-center gap-3">
            <button data-testid="driver-refresh" onClick={loadTrips} className="w-9 h-9 rounded-full border border-border flex items-center justify-center"><RefreshCw className="w-4 h-4" /></button>
            <button data-testid="driver-logout" onClick={logout} className="inline-flex items-center gap-1.5 text-sm rounded-full border border-border px-3 py-2"><LogOut className="w-4 h-4" /> Out</button>
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-5 py-6">
        <h1 className="font-heading font-extrabold text-2xl">Hi, {driver.name}</h1>
        <p className="text-sm text-muted-foreground mt-1">{trips.length} trip{trips.length === 1 ? "" : "s"} assigned to you.</p>

        {sharingTn && (
          <div data-testid="driver-sharing-banner" className="mt-5 flex items-center gap-2 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-500 px-4 py-3 text-sm">
            <Radio className="w-4 h-4 animate-pulse" /> Sharing live location for <b className="font-mono-x">{sharingTn}</b>
            <button data-testid="driver-stop-sharing" onClick={stopSharing} className="ml-auto text-xs underline">Stop</button>
          </div>
        )}

        <div className="mt-6 space-y-4" data-testid="driver-trips">
          {trips.length === 0 && (
            <div className="rounded-2xl border border-border bg-card/30 p-8 text-center text-muted-foreground text-sm">
              No trips assigned yet. Your dispatcher will assign one soon.
            </div>
          )}
          {trips.map((t) => (
            <div key={t.tracking_number} data-testid={`driver-trip-${t.tracking_number}`} className="rounded-2xl border border-border bg-card/40 p-5">
              <div className="flex items-center justify-between">
                <div className="font-mono-x text-xs text-muted-foreground">{t.tracking_number}</div>
                <span className={`text-xs font-semibold rounded-full px-2.5 py-1 border ${t.status === "Delivered" ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" : "bg-primary/10 text-primary border-primary/20"}`}>{t.status}</span>
              </div>
              <div className="mt-2 font-heading font-bold text-lg flex items-center gap-2">
                {t.origin} <ArrowRight className="w-4 h-4 text-primary" /> {t.destination}
              </div>
              <div className="mt-1 text-sm text-muted-foreground flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {t.cargo || t.route_name}</div>

              <div className="mt-4 flex items-center gap-1.5">
                {t.stops.map((s, i) => (
                  <span key={i} title={s} className={`h-1.5 flex-1 rounded-full ${i <= t.current_index ? "bg-primary" : "bg-border"}`} />
                ))}
              </div>
              <div className="mt-1.5 text-xs text-muted-foreground">Currently near <b>{t.stops[t.current_index]}</b></div>

              <div className="mt-5 grid grid-cols-2 gap-2.5">
                {!t.trip_started ? (
                  <button data-testid={`driver-start-${t.tracking_number}`} onClick={() => startTrip(t.tracking_number)}
                    className="col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-3.5">
                    <Play className="w-4 h-4" /> Start Trip
                  </button>
                ) : (
                  <>
                    {sharingTn === t.tracking_number ? (
                      <button data-testid={`driver-share-off-${t.tracking_number}`} onClick={stopSharing}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-border font-semibold py-3.5">
                        <Radio className="w-4 h-4" /> Stop sharing
                      </button>
                    ) : (
                      <button data-testid={`driver-share-on-${t.tracking_number}`} onClick={() => startSharing(t.tracking_number)}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-primary text-primary font-semibold py-3.5">
                        <Navigation className="w-4 h-4" /> Share location
                      </button>
                    )}
                    <button data-testid={`driver-advance-${t.tracking_number}`} onClick={() => advance(t)} disabled={t.status === "Delivered"}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-3.5 disabled:opacity-50">
                      <CheckCircle2 className="w-4 h-4" /> {t.current_index >= t.stops.length - 2 ? "Mark delivered" : "Next stop"}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
